import { cleanTranscript, detectNoteTypes, generateNote } from '../services/openAIService';
import { generateCortiNote } from '../services/cortiService';
import { countPopulatedVitals, hasFluidEntries, validateNote } from './jsonValidator';
import { getPatientById } from '../constants/mockPatients';
import {
  applyKnownPatientToNote,
  toNotePatientContext,
  applyPatientToGenerationResult,
} from './patientContext';
import { resolvePatientMatch } from './patientMatch';
import {
  hasDocumentableClinicalContent,
  isMetaPlaceholderText,
  transcriptHasClinicalSignal,
} from './clinicalContent';
import {
  applyKnownPatientToHandoverNote,
  enrichHandoverPatientsFromRoster,
} from './handoverUtils';
import { ADMISSIONS_ENABLED } from '../constants/features';
import { applyLateEntryMetadata } from './lateEntry';

const CLEANUP_FLAG = 'Cleanup unavailable — transcript shown in original language.';

export const DEFAULT_MAR_PROGRESS_SOAP = {
  subjective: '',
  objective: '',
  assessment: '',
  plan: '',
};

function applyCleanupFlag(note, cleanupFailed) {
  if (!note || !cleanupFailed) return note;
  return {
    ...note,
    flags: [...(note.flags || []), CLEANUP_FLAG],
  };
}

function hasMedications(note) {
  const meds = note?.medications_administered?.length
    ? note.medications_administered
    : note?.medications || [];
  return meds.length > 0;
}

function hasSubstantiveSoapSection(value) {
  return Boolean(String(value || '').trim()) && !isMetaPlaceholderText(value);
}

function hasAnySubstantiveSoap(soap = {}) {
  return ['subjective', 'objective', 'assessment', 'plan'].some((key) =>
    hasSubstantiveSoapSection(soap[key])
  );
}

/** Fill default SOAP when a MAR companion progress note has no clinical observation. */
export function applyMarCompanionProgressDefaults(note) {
  if (!note) return null;
  const soap = note.soap || {};
  if (hasAnySubstantiveSoap(soap) && note.clinical_content !== false) {
    return { ...note, clinical_content: true };
  }

  return {
    ...note,
    clinical_content: true,
    soap: {
      subjective: hasSubstantiveSoapSection(soap.subjective)
        ? soap.subjective
        : DEFAULT_MAR_PROGRESS_SOAP.subjective,
      objective: soap.objective || DEFAULT_MAR_PROGRESS_SOAP.objective,
      assessment: hasSubstantiveSoapSection(soap.assessment)
        ? soap.assessment
        : DEFAULT_MAR_PROGRESS_SOAP.assessment,
      plan: hasSubstantiveSoapSection(soap.plan)
        ? soap.plan
        : DEFAULT_MAR_PROGRESS_SOAP.plan,
    },
  };
}

function buildDefaultMarProgressNote({ encounterIso, patientContext = null }) {
  const base = {
    note_type: 'progress_note',
    patient: patientContext
      ? {
          name: patientContext.name || null,
          room: patientContext.room || null,
          age: patientContext.age || null,
          sex: patientContext.sex || null,
          details_source: 'transcript',
        }
      : {
          name: null,
          room: null,
          age: null,
          sex: null,
          details_source: 'transcript',
        },
    datetime: encounterIso || new Date().toISOString(),
    soap: { ...DEFAULT_MAR_PROGRESS_SOAP },
    vitals_extracted: {
      bp: null,
      pulse: null,
      temp_celsius: null,
      o2_sat: null,
      rr: null,
      pain: null,
    },
    negations_detected: [],
    outstanding_tasks: [],
    flags: [],
    clinical_content: true,
  };
  return patientContext ? applyKnownPatientToNote(base, patientContext) : base;
}

/**
 * When medications are detected — always generate progress_note alongside medication_admin
 * regardless of whether the detector returned progress_note on its own.
 */
export function ensureProgressNoteWithMedications(noteTypes = []) {
  const types = Array.isArray(noteTypes) ? [...noteTypes] : [];
  if (types.includes('care_plan')) {
    return types.filter((type) => type !== 'progress_note');
  }
  if (!types.includes('medication_admin')) return types;
  if (types.includes('progress_note')) return types;
  return ['progress_note', ...types];
}

function shouldIncludeGeneratedNote(type, note) {
  if (!note) return false;
  if (!hasDocumentableClinicalContent(note, type)) return false;
  if (type === 'vitals') return countPopulatedVitals(note) >= 1;
  if (type === 'medication_admin') return hasMedications(note);
  if (type === 'fluid_balance') return hasFluidEntries(note);
  if (type === 'care_plan') {
    const plan = note.care_plan || {};
    const hasDiagnosis = Boolean(String(plan.nursing_diagnosis || '').trim());
    const hasGoals = Boolean(String(plan.goals || '').trim());
    const hasInterventions = (plan.interventions || []).some((item) => String(item || '').trim());
    const hasEvaluation = Boolean(String(plan.evaluation || '').trim());
    return hasDiagnosis || hasGoals || hasInterventions || hasEvaluation;
  }
  return true;
}

function isVitalsOnlyTranscript(transcript) {
  if (!transcript) return false;
  const text = transcript.toLowerCase();
  const hasVitalsSignals =
    /(bp|blood pressure|pulse|temperature|temp|o2|oxygen|spo2|pain|mmhg|bpm)/i.test(text);
  if (!hasVitalsSignals) return false;

  const narrativeSignals =
    /(assessment|plan|improv|worse|complain|diagnos|treat|follow up|handover|medication|post-op|admission|discharge)/i;
  return !narrativeSignals.test(text);
}

export async function buildGenerationResult({
  rawTranscript,
  cleanedTranscript: precleanedTranscript = null,
  cleanupFailed: precleanedFailed = false,
  admittedPatients = [],
  patientId = null,
  forcedNoteTypes = null,
  admissionMeta = null,
  encounterIso,
  encounterGroupId,
}) {
  const raw = String(rawTranscript || '').trim();
  if (!raw) return null;

  let cleanedTranscript = precleanedTranscript || raw;
  let cleanupFailed = precleanedFailed;

  if (!precleanedTranscript) {
    try {
      cleanedTranscript = await cleanTranscript(raw);
      if (!cleanedTranscript?.trim()) {
        cleanedTranscript = raw;
        cleanupFailed = true;
      }
    } catch {
      cleanedTranscript = raw;
      cleanupFailed = true;
    }
  }

  if (!transcriptHasClinicalSignal(raw, cleanedTranscript)) {
    return { error: 'no_clinical_content' };
  }

  let noteTypes = [];
  let activeForcedNoteTypes = forcedNoteTypes;
  if (!ADMISSIONS_ENABLED && activeForcedNoteTypes?.includes('admission')) {
    activeForcedNoteTypes = null;
  }

  try {
    noteTypes = activeForcedNoteTypes?.length
      ? activeForcedNoteTypes
      : await detectNoteTypes(cleanedTranscript, raw);
  } catch {
    noteTypes = [];
  }

  // When medications are detected — always run generateNote for both medication_admin and progress_note.
  noteTypes = ensureProgressNoteWithMedications(noteTypes);

  if (!noteTypes.length) {
    return { error: 'no_clinical_content' };
  }

  if (
    !activeForcedNoteTypes?.length &&
    noteTypes.includes('progress_note') &&
    noteTypes.includes('vitals') &&
    !noteTypes.includes('medication_admin') &&
    isVitalsOnlyTranscript(cleanedTranscript)
  ) {
    noteTypes = noteTypes.filter((type) => type !== 'progress_note');
  }

  const knownPatient = patientId ? getPatientById(patientId, admittedPatients) : null;
  let patientContext = knownPatient ? toNotePatientContext(knownPatient) : null;
  let resolvedPatientId = patientId || null;

  if (admissionMeta && activeForcedNoteTypes?.includes('admission')) {
    const header = `Patient ${admissionMeta.name}, date of birth ${admissionMeta.dob}, ${admissionMeta.sex}.`;
    cleanedTranscript = `${header}\n${cleanedTranscript}`;
  }

  const generationResults = await Promise.all(
    noteTypes.map(async (type) => {
      try {
        const rawNote = await generateNote(cleanedTranscript, type);
        let validated = validateNote(rawNote, type);
        if (patientContext) {
          validated =
            type === 'handover'
              ? applyKnownPatientToHandoverNote(validated, patientContext)
              : applyKnownPatientToNote(validated, patientContext);
        } else if (type === 'handover') {
          validated = enrichHandoverPatientsFromRoster(validated, {
            admittedPatients,
            transcript: cleanedTranscript,
          });
        }
        return {
          type,
          note: applyCleanupFlag(validated, cleanupFailed),
          error: null,
        };
      } catch (err) {
        return { type, note: null, error: err.message };
      }
    })
  );

  const medicationsRequested = noteTypes.includes('medication_admin');
  const generatedNotes = [];
  for (const result of generationResults) {
    if (result.error) {
      generatedNotes.push({ type: result.type, note: null, error: result.error });
      continue;
    }
    let note = result.note;
    if (medicationsRequested && result.type === 'progress_note' && note) {
      note = applyMarCompanionProgressDefaults(note);
    }
    if (!shouldIncludeGeneratedNote(result.type, note)) continue;
    const documentationTime = encounterIso || note.datetime || new Date().toISOString();
    const withTiming = applyLateEntryMetadata(
      { ...note, datetime: note.datetime || documentationTime },
      documentationTime
    );
    generatedNotes.push({
      type: result.type,
      note: withTiming,
      error: null,
    });
  }

  const hasMarNote = generatedNotes.some(
    (entry) => entry.type === 'medication_admin' && entry.note
  );
  const hasCarePlanNote = generatedNotes.some((entry) => entry.type === 'care_plan' && entry.note);
  if (
    hasMarNote
    && !hasCarePlanNote
    && !generatedNotes.some((entry) => entry.type === 'progress_note' && entry.note)
  ) {
    const progressIndex = generatedNotes.findIndex((entry) => entry.type === 'progress_note');
    const defaultEntry = {
      type: 'progress_note',
      note: applyCleanupFlag(
        buildDefaultMarProgressNote({ encounterIso, patientContext }),
        cleanupFailed
      ),
      error: null,
    };
    if (progressIndex === -1) {
      generatedNotes.unshift(defaultEntry);
    } else {
      generatedNotes[progressIndex] = defaultEntry;
    }
  }

  if (!generatedNotes.some((entry) => entry.note)) {
    return { error: 'no_clinical_content' };
  }

  const noteType = generatedNotes[0]?.type || noteTypes[0] || 'progress_note';
  const openaiError = generatedNotes[0]?.error || null;
  const companionVitalsNote =
    generatedNotes.find((entry) => entry.type === 'vitals' && entry.note)?.note || null;

  let cortiNote = null;
  let cortiResult = { failed: true, raw: null, reason: 'Skipped' };

  if (
    generatedNotes.length === 1 &&
    generatedNotes[0].note &&
    !['vitals', 'medication_admin', 'fluid_balance', 'care_plan'].includes(generatedNotes[0].type)
  ) {
    cortiResult = await generateCortiNote(cleanedTranscript, generatedNotes[0].type);
    if (!cortiResult.failed && cortiResult.note) {
      cortiNote = validateNote(cortiResult.note, generatedNotes[0].type);
      if (patientContext) {
        cortiNote = applyKnownPatientToNote(cortiNote, patientContext);
      }
      cortiNote.datetime = cortiNote.datetime || encounterIso;
    }
  }

  if (!patientContext && generatedNotes.length) {
    const hintNote = generatedNotes.find((entry) => entry.note)?.note;
    const handoverPatientCount = hintNote?.patients?.length || 0;
    const isMultiPatientHandover = handoverPatientCount > 1;
    const patientHints = hintNote?.patients?.[0]
      ? { name: hintNote.patients[0].patient_name, room: hintNote.patients[0].room }
      : hintNote?.patient;

    if (!isMultiPatientHandover) {
      const match = resolvePatientMatch({
        admittedPatients,
        transcript: cleanedTranscript,
        patientHints,
        patientId,
      });

      if (match.status === 'resolved' && match.patient) {
        patientContext = toNotePatientContext(match.patient);
        resolvedPatientId = match.patient.id;
        for (let i = 0; i < generatedNotes.length; i += 1) {
          const entry = generatedNotes[i];
          if (!entry.note) continue;
          generatedNotes[i] = {
            ...entry,
            note:
              entry.type === 'handover'
                ? applyKnownPatientToHandoverNote(entry.note, patientContext)
                : applyKnownPatientToNote(entry.note, patientContext),
          };
        }
        if (cortiNote) {
          cortiNote = applyKnownPatientToNote(cortiNote, patientContext);
        }
      }
    }

    if (!resolvedPatientId) {
      for (let i = 0; i < generatedNotes.length; i += 1) {
        const entry = generatedNotes[i];
        if (!entry.note || entry.type !== 'handover') continue;
        generatedNotes[i] = {
          ...entry,
          note: enrichHandoverPatientsFromRoster(entry.note, {
            admittedPatients,
            transcript: cleanedTranscript,
          }),
        };
      }
    }
  }

  const resolvedOpenaiNote =
    generatedNotes.find((entry) => entry.note)?.note || generatedNotes[0]?.note || null;

  const result = {
    noteId: encounterGroupId,
    encounterGroupId,
    noteType,
    generatedNotes,
    openaiNote: resolvedOpenaiNote,
    openaiError,
    companionVitalsNote,
    cortiNote,
    cortiRaw: cortiResult.raw,
    cortiFailed: cortiResult.failed,
    cortiError: cortiResult.reason,
    transcriptRaw: raw,
    transcriptCleaned: cleanedTranscript,
    cleanupFailed,
    encounterDateTime: encounterIso,
    patientId: resolvedPatientId,
  };

  const hintNote = generatedNotes.find((entry) => entry.note)?.note;
  const patientHints = hintNote?.patients?.[0]
    ? { name: hintNote.patients[0].patient_name, room: hintNote.patients[0].room }
    : hintNote?.patient;
  const isMultiPatientHandover = (hintNote?.patients?.length || 0) > 1;

  if (!patientId && !resolvedPatientId && !isMultiPatientHandover) {
    const match = resolvePatientMatch({
      admittedPatients,
      transcript: cleanedTranscript,
      patientHints,
    });

    if (match.status === 'resolved' && match.patient) {
      return applyPatientToGenerationResult(result, match.patient);
    }

    return {
      ...result,
      needsPatientSelection: true,
      patientMatch: match,
      patientHints,
    };
  }

  if (knownPatient) {
    return applyPatientToGenerationResult(result, knownPatient);
  }

  return result;
}
